import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { SettingsProvider } from './contexts/SettingsContext'
import { CurrencyProvider } from './contexts/CurrencyContext'
import { AuthProvider } from './contexts/AuthContext'
import { PageActionProvider } from './contexts/PageActionContext'
import App from './App.jsx'
import './index.css'

// Global error boundary to catch any runtime crash and show the actual error
class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    this.setState({ info });
    console.error('🔴 GLOBAL ERROR BOUNDARY CAUGHT:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ fontFamily: 'monospace', padding: '32px', background: '#fff', minHeight: '100vh' }}>
          <h1 style={{ color: '#dc2626', fontSize: '20px', marginBottom: '12px' }}>⚠️ App Crashed — Error Details</h1>
          <pre style={{ background: '#fef2f2', border: '1px solid #fca5a5', padding: '16px', borderRadius: '8px', fontSize: '13px', overflowX: 'auto', whiteSpace: 'pre-wrap', color: '#7f1d1d' }}>
            {this.state.error?.toString()}
            {'\n\n'}
            {this.state.error?.stack}
          </pre>
          {this.state.info && (
            <pre style={{ marginTop: '16px', background: '#eff6ff', border: '1px solid #93c5fd', padding: '16px', borderRadius: '8px', fontSize: '12px', overflowX: 'auto', whiteSpace: 'pre-wrap', color: '#1e3a8a' }}>
              {this.state.info.componentStack}
            </pre>
          )}
          <button
            onClick={() => { this.setState({ hasError: false, error: null, info: null }); window.location.reload(); }}
            style={{ marginTop: '20px', padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
          >
            Reload App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')).render(
  <GlobalErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <CurrencyProvider>
              <PageActionProvider>
                <App />
                <Toaster
                  position="bottom-left"
                  containerStyle={{
                    bottom: 24,
                    left: 24,
                    zIndex: 99999,
                  }}
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#1e293b',
                      color: '#f8fafc',
                      borderRadius: '10px',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      fontSize: '13.5px',
                      fontWeight: '500',
                    },
                    success: { duration: 3000, iconTheme: { primary: '#22c55e', secondary: '#fff' } },
                    error: { duration: 5000, iconTheme: { primary: '#ef4444', secondary: '#fff' } },
                  }}
                />
              </PageActionProvider>
            </CurrencyProvider>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </GlobalErrorBoundary>
)